/**
 * Agent evals v2 wire contract (docs/exec-plans/active/agent-evals-v2.md).
 *
 * A scenario owns its fixture, trigger, golden and scoring. The golden's
 * kind decides the scorer; scoring for record goldens is a comparator table,
 * never a script.
 */
import { z } from "zod";
import { AgentEvalScoreDetailSchema } from "./eval-score-detail";
import { zOpaqueId, zPublicId } from "./zod-format";

export const AGENT_EVAL_COMPARATORS = [
  // Literal JSON equality; tolerant comparisons must be chosen explicitly.
  "exact",
  "number",
  "date",
  "address",
  "vocabulary",
  "default_tolerant",
] as const;
export type AgentEvalComparator = (typeof AGENT_EVAL_COMPARATORS)[number];

export const AGENT_EVAL_SCENARIO_STATUSES = ["draft", "minted", "archived"] as const;
export type AgentEvalScenarioStatus = (typeof AGENT_EVAL_SCENARIO_STATUSES)[number];

export const AGENT_EVAL_RUN_KINDS = ["trial", "permanent"] as const;
export const AGENT_EVAL_RUN_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
] as const;
export type AgentEvalRunStatus = (typeof AGENT_EVAL_RUN_STATUSES)[number];
/** A result, unlike a run, can also end at the first human-input request when the fixture says to stop there. */
export const AGENT_EVAL_RESULT_STATUSES = [...AGENT_EVAL_RUN_STATUSES, "stopped"] as const;
export type AgentEvalResultStatus = (typeof AGENT_EVAL_RESULT_STATUSES)[number];

/** One field's comparator, on a scenario (override) or a custom model (default). */
export const AgentEvalFieldPolicySchema = z
  .object({
    comparator: z.enum(AGENT_EVAL_COMPARATORS),
    weight: z.number().min(0).max(100).optional(),
    /** vocabulary: groups of spellings that mean the same value. */
    aliases: z
      .array(z.array(z.string().min(1).max(200)).min(1).max(50))
      .max(200)
      .optional(),
    /** default_tolerant: the sibling field whose value "default" makes any produced value acceptable. */
    basis_field: z.string().min(1).max(256).optional(),
    /** number: absolute tolerance; defaults to 0.005. */
    tolerance: z.number().min(0).optional(),
    /** Why the policy is what it is, in words. Shown on the scoring table. */
    note: z.string().max(1000).optional(),
  })
  .strict();
export type AgentEvalFieldPolicy = z.infer<typeof AgentEvalFieldPolicySchema>;

/** custom_models.eval_field_policies_json */
export const AgentEvalModelFieldPoliciesSchema = z.record(
  z.string().min(1).max(256),
  AgentEvalFieldPolicySchema,
);
export type AgentEvalModelFieldPolicies = z.infer<typeof AgentEvalModelFieldPoliciesSchema>;

/** agent_eval_pinned_inputs.scoring_json: the workspace model each records section resolved to at admission. */
export const AgentEvalPinnedScoringSchema = z
  .object({
    models: z.record(
      z.string(),
      z
        .object({
          public_id: z.string(),
          name: z.string(),
          field_policies: AgentEvalModelFieldPoliciesSchema.nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type AgentEvalPinnedScoring = z.infer<typeof AgentEvalPinnedScoringSchema>;

export const AgentEvalScoringFieldSchema = AgentEvalFieldPolicySchema.extend({
  name: z.string().min(1).max(256),
}).strict();
export type AgentEvalScoringField = z.infer<typeof AgentEvalScoringFieldSchema>;

/** How one golden section's rows are compared with the produced rows of the same kind. */
export const AgentEvalRecordsScoringSchema = z
  .object({
    /** Tried in order; a golden row matches the first produced row that agrees on every key. */
    match_keys: z
      .array(z.array(z.string().min(1).max(256)).min(1).max(8))
      .min(1)
      .max(8),
    fields: z.array(AgentEvalScoringFieldSchema).max(500).default([]),
    /** Present in the golden but never scored (free prose, provenance). */
    ignored: z.array(z.string().min(1).max(256)).max(500).default([]),
    /** Unmatched produced rows that are not counted as extras. */
    extras_ignore: z
      .array(
        z
          .object({
            field: z.string().min(1).max(256),
            equals: z.union([z.string(), z.number(), z.boolean()]).optional(),
            in: z.array(z.string()).max(100).optional(),
          })
          .strict(),
      )
      .max(50)
      .optional(),
    /**
     * Fields whose value is another custom record's id (`x-quovy-ref:
     * {type: "custom_record"}`) and the records section that record belongs
     * to. A golden row names the referenced record by that section's expected
     * row (`{"$row": 0}` or `{"$where": {…}}`) and the scorer substitutes the
     * id of the produced record matched to that row, so references to
     * run-minted records match and score. A plain string still means a literal
     * id (a seeded fixture record).
     */
    references: z
      .array(
        z
          .object({
            field: z.string().min(1).max(256),
            custom_model_id: zOpaqueId("fixture_ref"),
          })
          .strict(),
      )
      .max(50)
      .optional(),
  })
  .strict();
export type AgentEvalRecordsScoring = z.infer<typeof AgentEvalRecordsScoringSchema>;

const goldenReferenceScalar = z.union([z.string(), z.number(), z.boolean()]);

/**
 * A golden cell that points at another records section's expected row:
 * `{"$row": <0-based index, as expected_index>}` or `{"$where": {<field>:
 * <value>, …}}`, which must pick out exactly one expected row of that section
 * (values compare with that section's comparators, like match keys).
 */
export const AgentEvalGoldenReferenceSchema = z.union([
  z.object({ $row: z.number().int().min(0) }).strict(),
  z
    .object({
      $where: z
        .record(z.string().min(1).max(256), goldenReferenceScalar)
        .refine((where) => Object.keys(where).length >= 1 && Object.keys(where).length <= 8, {
          message: "$where needs 1 to 8 fields of the referenced expected row.",
        }),
    })
    .strict(),
]);
export type AgentEvalGoldenReference = z.infer<typeof AgentEvalGoldenReferenceSchema>;

/** True for a plain object whose keys are `$`-prefixed: a reference directive, valid or not. */
export function isGoldenReferenceDirective(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => key.startsWith("$"));
}

/** The task fields a produced task is scored on; golden task rows use the same names. */
export const AGENT_EVAL_TASK_FIELDS = [
  "assignee_agent_id",
  "title",
  "description",
  "status",
  "priority",
] as const;
export const AGENT_EVAL_TASKS_DEFAULT_MATCH_KEYS: string[][] = [["assignee_agent_id"]];
export const AGENT_EVAL_TASKS_DEFAULT_WEIGHT = 0.25;

/** Tags always match one resource and compare its complete, unordered tag set. */
export const AGENT_EVAL_TAGS_SCORING: AgentEvalRecordsScoring = {
  match_keys: [["resource_id"]],
  fields: [{ name: "tags", comparator: "exact" }],
  ignored: [],
};

/**
 * A link names its parent task by the task's own attributes because created
 * task ids are run-minted. A pinned `resource_id` matches first; a row
 * that omits it falls back to parent + resource type, picking the closest
 * candidate by label and purpose so a wrong label is a mismatch, not a miss.
 * Seeded links the golden does not name are world state, not extras.
 */
export const AGENT_EVAL_LINKED_RESOURCES_SCORING: AgentEvalRecordsScoring = {
  match_keys: [
    ["assignee_agent_id", "resource_type", "resource_id"],
    ["title", "resource_type", "resource_id"],
    ["assignee_agent_id", "resource_type"],
    ["title", "resource_type"],
  ],
  fields: [],
  ignored: [],
  extras_ignore: [{ field: "origin", equals: "seeded" }],
};
export const AGENT_EVAL_LINKED_RESOURCES_DEFAULT_WEIGHT = 0.25;

/** Stored goldens carry their shape version; `agent_eval_golden_v1_to_v2()` in migration 0292 is the upgrade from the unversioned shape. */
export const AGENT_EVAL_GOLDEN_VERSION = 2;
export const AGENT_EVAL_GOLDEN_SECTION_KINDS = [
  "records",
  "tasks",
  "tags",
  "linked_resources",
] as const;
export type AgentEvalGoldenSectionKind = (typeof AGENT_EVAL_GOLDEN_SECTION_KINDS)[number];

const goldenRows = (max: number) =>
  // Omission means unknown; an explicit blank requires abstention.
  z.array(z.object({ data: z.record(z.string(), z.unknown()) }).strict()).max(max);

/** The rows the agent should have written to one custom model, and how they are compared. */
export const AgentEvalRecordsGoldenSectionSchema = z
  .object({
    kind: z.literal("records"),
    /** A fixture model id or a cmdl_ public id. */
    custom_model_id: zOpaqueId("fixture_ref"),
    /** This section's share of the score next to the other sections'. */
    weight: z.number().min(0).max(10).default(1),
    rows: goldenRows(5000),
    scoring: AgentEvalRecordsScoringSchema,
  })
  .strict();
export type AgentEvalRecordsGoldenSection = z.infer<typeof AgentEvalRecordsGoldenSectionSchema>;

/** The tasks the agent should have created, as rows over AGENT_EVAL_TASK_FIELDS; seeded tasks are the world, not the work. */
export const AgentEvalTasksGoldenSectionSchema = z
  .object({
    kind: z.literal("tasks"),
    weight: z.number().min(0).max(10).default(AGENT_EVAL_TASKS_DEFAULT_WEIGHT),
    rows: goldenRows(200),
    scoring: AgentEvalRecordsScoringSchema.omit({ references: true })
      .extend({
        match_keys: z
          .array(z.array(z.string().min(1).max(256)).min(1).max(8))
          .min(1)
          .max(8)
          .default(AGENT_EVAL_TASKS_DEFAULT_MATCH_KEYS),
      })
      .strict()
      .default({ match_keys: AGENT_EVAL_TASKS_DEFAULT_MATCH_KEYS, fields: [], ignored: [] }),
  })
  .strict();
export type AgentEvalTasksGoldenSection = z.infer<typeof AgentEvalTasksGoldenSectionSchema>;

/** The exact final tag set expected on each fixture resource. */
export const AgentEvalTagsGoldenSectionSchema = z
  .object({
    kind: z.literal("tags"),
    weight: z.number().min(0).max(10).default(1),
    rows: z
      .array(
        z
          .object({
            data: z
              .object({
                resource_id: zOpaqueId("fixture_ref"),
                tags: z.array(z.string().min(1).max(255)).max(500),
              })
              .strict(),
          })
          .strict(),
      )
      .max(5000),
  })
  .strict()
  .superRefine((section, ctx) => {
    const resources = new Set<string>();
    section.rows.forEach((row, rowIndex) => {
      if (resources.has(row.data.resource_id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows", rowIndex, "data", "resource_id"],
          message: `Resource ${row.data.resource_id} appears twice; merge its expected tags into one row.`,
        });
      }
      resources.add(row.data.resource_id);
      if (new Set(row.data.tags).size !== row.data.tags.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows", rowIndex, "data", "tags"],
          message: `Resource ${row.data.resource_id} contains duplicate expected tags; remove duplicates.`,
        });
      }
    });
  });
export type AgentEvalTagsGoldenSection = z.infer<typeof AgentEvalTagsGoldenSectionSchema>;

const nullableText = (max: number) => z.string().max(max).nullable().optional();

/**
 * The links expected on tasks after the run, one row per link. A key the row
 * omits is not graded, so a row can pin a seeded `emsg_` id or leave a
 * run-minted `crec_` id out and grade only its type and label.
 */
export const AgentEvalLinkedResourcesGoldenSectionSchema = z
  .object({
    kind: z.literal("linked_resources"),
    weight: z.number().min(0).max(10).default(AGENT_EVAL_LINKED_RESOURCES_DEFAULT_WEIGHT),
    rows: z
      .array(
        z
          .object({
            data: z
              .object({
                assignee_agent_id: zOpaqueId("fixture_ref").nullable().optional(),
                title: nullableText(255),
                resource_type: z.string().min(1).max(64),
                resource_id: zOpaqueId("fixture_ref").optional(),
                label: nullableText(255),
                purpose: nullableText(64),
                origin: z.enum(["seeded", "created"]).optional(),
              })
              .strict(),
          })
          .strict(),
      )
      .max(2000),
  })
  .strict()
  .superRefine((section, ctx) => {
    section.rows.forEach((row, rowIndex) => {
      if (!row.data.assignee_agent_id && !row.data.title) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows", rowIndex, "data"],
          message:
            "A linked resource row names no parent task. Add the task's assignee_agent_id or title.",
        });
      }
    });
  });
export type AgentEvalLinkedResourcesGoldenSection = z.infer<
  typeof AgentEvalLinkedResourcesGoldenSectionSchema
>;

export const AgentEvalGoldenSectionSchema = z.discriminatedUnion("kind", [
  AgentEvalRecordsGoldenSectionSchema,
  AgentEvalTasksGoldenSectionSchema,
  AgentEvalTagsGoldenSectionSchema,
  AgentEvalLinkedResourcesGoldenSectionSchema,
]);
export type AgentEvalGoldenSection = z.infer<typeof AgentEvalGoldenSectionSchema>;

/** Identifies a section within its golden and its score detail: one tasks section, one records section per model. */
export function goldenSectionKey(section: {
  kind: AgentEvalGoldenSectionKind;
  custom_model_id?: string;
}): string {
  return section.kind === "records" ? `records:${section.custom_model_id}` : section.kind;
}

/**
 * agent_eval_scenarios.golden_json: the correct end state as one section per
 * kind of state the agent produces, each with its own comparison rules. The
 * score is the weighted mean of the sections.
 */
export const AgentEvalGoldenSchema = z
  .object({
    version: z.literal(AGENT_EVAL_GOLDEN_VERSION),
    sections: z.array(AgentEvalGoldenSectionSchema).min(1).max(8),
  })
  .strict()
  .superRefine((golden, ctx) => {
    const seen = new Set<string>();
    golden.sections.forEach((section, index) => {
      const key = goldenSectionKey(section);
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", index],
          message: `Section ${key} appears twice; merge the rows into one section.`,
        });
      }
      seen.add(key);
    });
    validateGoldenReferences(golden.sections, ctx);
  });
export type AgentEvalGolden = z.infer<typeof AgentEvalGoldenSchema>;

/**
 * Reference fields name another records section of the same golden, cells
 * hold a literal id, a blank or a valid directive, and the sections'
 * references form no cycle (the scorer matches referenced sections first).
 */
function validateGoldenReferences(sections: AgentEvalGoldenSection[], ctx: z.RefinementCtx): void {
  const records = new Map<string, { index: number; section: AgentEvalRecordsGoldenSection }>();
  sections.forEach((section, index) => {
    if (section.kind === "records") records.set(section.custom_model_id, { index, section });
  });
  const edges = new Map<string, string[]>();
  for (const [modelId, { index, section }] of records) {
    const references = section.scoring.references ?? [];
    const fields = new Map<string, string>();
    references.forEach((reference, refIndex) => {
      const path = ["sections", index, "scoring", "references", refIndex];
      if (fields.has(reference.field)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `Reference field ${reference.field} is declared twice; keep one entry.`,
        });
      }
      fields.set(reference.field, reference.custom_model_id);
      if (reference.custom_model_id === modelId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `Reference field ${reference.field} points at its own section; a section can only reference another records section.`,
        });
      } else if (!records.has(reference.custom_model_id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `Reference field ${reference.field} points at ${reference.custom_model_id}, which has no records section in this golden. Add that section or use the same custom_model_id as its section.`,
        });
      }
    });
    edges.set(
      modelId,
      [...fields.values()].filter((target) => target !== modelId && records.has(target)),
    );
    section.rows.forEach((row, rowIndex) => {
      for (const [field, value] of Object.entries(row.data)) {
        const path = ["sections", index, "rows", rowIndex, "data", field];
        const target = fields.get(field);
        if (target === undefined) {
          if (isGoldenReferenceDirective(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path,
              message: `${field} holds a reference directive but is not in scoring.references. Declare {field: "${field}", custom_model_id: "<referenced section>"} or write a literal value.`,
            });
          }
          continue;
        }
        if (value === null || value === undefined || typeof value === "string") continue;
        const parsed = AgentEvalGoldenReferenceSchema.safeParse(value);
        if (!parsed.success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: `${field} is a reference: write a literal record id, null, {"$row": <expected row index>} or {"$where": {<field>: <value>}} naming one expected row of ${target}.`,
          });
          continue;
        }
        const targetRows = records.get(target)?.section.rows.length;
        if ("$row" in parsed.data && targetRows !== undefined && parsed.data.$row >= targetRows) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: `${field} references expected row ${parsed.data.$row} of ${target}, which has ${targetRows} rows (indexes start at 0).`,
          });
        }
      }
    });
  }
  // Depth-first search for a cycle among referencing sections.
  const state = new Map<string, "visiting" | "done">();
  const visit = (modelId: string, trail: string[]): boolean => {
    if (state.get(modelId) === "done") return false;
    if (state.get(modelId) === "visiting") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sections", records.get(modelId)!.index, "scoring", "references"],
        message: `Records sections reference each other in a cycle (${[...trail, modelId].join(" -> ")}); one side must name the other by a literal value instead.`,
      });
      return true;
    }
    state.set(modelId, "visiting");
    for (const next of edges.get(modelId) ?? []) {
      if (visit(next, [...trail, modelId])) return true;
    }
    state.set(modelId, "done");
    return false;
  };
  for (const modelId of records.keys()) if (visit(modelId, [])) break;
}
export type AgentEvalGoldenInput = z.input<typeof AgentEvalGoldenSchema>;

export const AGENT_EVAL_TRIGGER_KINDS = [
  "message",
  "chat_followup",
  "task_assignment",
  "task_mention",
  "schedule",
  "subagent",
  "workflow_email",
] as const;
export type AgentEvalTriggerKind = (typeof AGENT_EVAL_TRIGGER_KINDS)[number];

export const AGENT_EVAL_TRIGGER_LABELS: Record<AgentEvalTriggerKind, string> = {
  message: "Message",
  chat_followup: "Chat follow-up",
  task_assignment: "Task assignment",
  task_mention: "Task mention",
  schedule: "Scheduled run",
  subagent: "Subagent brief",
  workflow_email: "Workflow email",
};

const AgentEvalTriggerTextSchema = z.string().min(1).max(20000);

export const AgentEvalChatTurnSchema = z
  .object({ role: z.enum(["user", "assistant"]), content: AgentEvalTriggerTextSchema })
  .strict();
export type AgentEvalChatTurn = z.infer<typeof AgentEvalChatTurnSchema>;

/**
 * The event that starts a scenario's agent thread. A trigger stores the event,
 * never a rendered message: the eval worker renders the opening turn with the
 * same code the live entry point uses, so a wording change in production cannot
 * leave the eval behind.
 */
export const AgentEvalTriggerSchema = z.discriminatedUnion("kind", [
  /** A user message, as chat or the public API sends it. `channel` is informational. */
  z
    .object({
      kind: z.literal("message"),
      message: AgentEvalTriggerTextSchema,
      channel: z.enum(["chat", "api"]).optional(),
    })
    .strict(),
  /** A follow-up in an existing chat: `history` is seeded into the thread before `message`. */
  z
    .object({
      kind: z.literal("chat_followup"),
      history: z.array(AgentEvalChatTurnSchema).min(1).max(50),
      message: AgentEvalTriggerTextSchema,
    })
    .strict(),
  /** The agent is assigned a task the fixture's `quovy.tasks` adapter seeds. */
  z
    .object({
      kind: z.literal("task_assignment"),
      task_id: zOpaqueId("fixture_ref"),
    })
    .strict(),
  /** A comment on a seeded task mentions the agent; the comment is the opening user turn. */
  z
    .object({
      kind: z.literal("task_mention"),
      task_id: zOpaqueId("fixture_ref"),
      comment: AgentEvalTriggerTextSchema,
    })
    .strict(),
  /** A scheduled run; `message` plays the routine's run prompt, absent means the runtime default. */
  z
    .object({ kind: z.literal("schedule"), message: AgentEvalTriggerTextSchema.optional() })
    .strict(),
  /** A delegation from a parent run; the brief is the opening message. */
  z.object({ kind: z.literal("subagent"), brief: AgentEvalTriggerTextSchema }).strict(),
  /** Interim until agents have direct email triggers: the message inside the fixture mailbox. */
  z
    .object({
      kind: z.literal("workflow_email"),
      mailbox_id: zOpaqueId("fixture_ref"),
      message_id: zOpaqueId("fixture_ref"),
      /** Optional literal prompt appended to the workflow's run_agent step input. */
      prompt: z.string().max(20000).optional(),
    })
    .strict(),
]);
export type AgentEvalTrigger = z.infer<typeof AgentEvalTriggerSchema>;

/** The text a trigger opens with, for one-line summaries; null when the runtime renders it. */
export function agentEvalTriggerExcerptSource(trigger: AgentEvalTrigger): string | null {
  switch (trigger.kind) {
    case "message":
    case "chat_followup":
      return trigger.message;
    case "task_mention":
      return trigger.comment;
    case "schedule":
      return trigger.message ?? null;
    case "subagent":
      return trigger.brief;
    case "workflow_email":
      return trigger.prompt ?? null;
    case "task_assignment":
      return null;
  }
}

export const AgentEvalSourceSchema = z
  .object({
    agent_thread_id: z.string().optional(),
    /** The grade this scenario was promoted from; its subject thread is `agent_thread_id`. */
    grade_id: z.string().optional(),
    verification_ids: z.array(z.string()).max(50).optional(),
    uploaded_file_ids: z.array(z.string()).max(200).optional(),
    legacy_scenario_id: z.string().optional(),
  })
  .strict();
export type AgentEvalSource = z.infer<typeof AgentEvalSourceSchema>;

const AgentEvalJsonObjectSchema = z.record(z.string(), z.unknown());

export function effectiveAgentEvalConstraintMin(input: {
  expect: "required" | "allowed" | "forbidden";
  min?: number | undefined;
}): number {
  return input.min ?? (input.expect === "required" ? 1 : 0);
}

export const AgentEvalHumanInputMatchSchema = z
  .object({
    request_kind: z.enum(["question", "approval"]).optional(),
    tool_name: z.string().min(1).max(255).optional(),
    input: z
      .object({
        match: z.enum(["subset", "exact"]),
        value: AgentEvalJsonObjectSchema,
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.tool_name && value.request_kind === "question") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tool_name"],
        message: "tool_name can match only an approval request.",
      });
    }
  });

export const AgentEvalHumanInputConstraintSchema = z
  .object({
    kind: z.literal("human_input"),
    expect: z.enum(["required", "allowed", "forbidden"]),
    match: AgentEvalHumanInputMatchSchema,
    min: z.number().int().nonnegative().max(100).optional(),
    max: z.number().int().nonnegative().max(100).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const min = effectiveAgentEvalConstraintMin(value);
    if (value.max !== undefined && min > value.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["max"],
        message: `max must be greater than or equal to min (${min}). Increase max or lower min.`,
      });
    }
    if (value.expect === "forbidden" && (value.min !== undefined || value.max !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "forbidden checks always mean exactly zero matches; omit min and max.",
      });
    }
  });
export type AgentEvalHumanInputConstraint = z.infer<typeof AgentEvalHumanInputConstraintSchema>;

export const AgentEvalConstraintsSchema = z
  .object({
    version: z.literal(1),
    checks: z.array(AgentEvalHumanInputConstraintSchema).max(100),
  })
  .strict();
export type AgentEvalConstraints = z.infer<typeof AgentEvalConstraintsSchema>;
export const EMPTY_AGENT_EVAL_CONSTRAINTS: AgentEvalConstraints = { version: 1, checks: [] };

export const AgentEvalHumanInputObservationSchema = z
  .object({
    kind: z.literal("human_input"),
    request_kind: z.enum(["question", "approval"]),
    tool_name: z.string().min(1).max(255).optional(),
    input: AgentEvalJsonObjectSchema,
    response: AgentEvalJsonObjectSchema.nullable(),
  })
  .strict();
export type AgentEvalHumanInputObservation = z.infer<typeof AgentEvalHumanInputObservationSchema>;

export const AgentEvalObservationsSchema = z
  .object({
    version: z.literal(1),
    human_input: z.array(AgentEvalHumanInputObservationSchema).max(100),
  })
  .strict();
export type AgentEvalObservations = z.infer<typeof AgentEvalObservationsSchema>;

export const AgentEvalConstraintCheckDetailSchema = z
  .object({
    check_index: z.number().int().nonnegative(),
    expect: z.enum(["required", "allowed", "forbidden"]),
    passed: z.boolean(),
    matched_count: z.number().int().nonnegative(),
    min: z.number().int().nonnegative(),
    max: z.number().int().nonnegative().nullable(),
    matched_observation_indexes: z.array(z.number().int().nonnegative()).max(100),
    reason: z.string().min(1).max(1000),
  })
  .strict();

export const AgentEvalConstraintDetailSchema = z
  .object({
    version: z.literal(1),
    passed: z.boolean(),
    checks: z.array(AgentEvalConstraintCheckDetailSchema).max(100),
  })
  .strict();
export type AgentEvalConstraintDetail = z.infer<typeof AgentEvalConstraintDetailSchema>;

export const AgentEvalSourceInputSchema = z
  .object({
    agent_thread_id: zPublicId("agent_thread").optional(),
    grade_id: zPublicId("grade").optional(),
    verification_ids: z.array(zPublicId("verification")).max(50).optional(),
    uploaded_file_ids: z.array(zPublicId("file")).max(200).optional(),
    legacy_scenario_id: zPublicId("eval_scenario").optional(),
  })
  .strict();

const AgentEvalScenarioCoreSchema = z.object({
  id: z.string(),
  agent_id: z.string(),
  /** Configuration name, unique within the agent; a release addresses the scenario by it. */
  slug: z.string(),
  name: z.string(),
  notes: z.string().nullable(),
  status: z.enum(AGENT_EVAL_SCENARIO_STATUSES),
  version: z.number().int().min(1),
  content_sha256: z.string(),
  source: AgentEvalSourceSchema.nullable(),
  minted_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** List row: no fixture / golden bodies. */
export const AgentEvalScenarioSchema = AgentEvalScenarioCoreSchema.extend({
  /** Latest permanent result score, for the list. */
  latest_score: z.number().min(0).max(1).nullable(),
}).strict();
export type AgentEvalScenarioWire = z.infer<typeof AgentEvalScenarioSchema>;

/** What the agent receives first, rendered the way the runtime will render it at run time. */
export const AgentEvalOpeningTurnSchema = z
  .object({
    source: z.enum(["author", "runtime"]),
    role: z.enum(["user", "system"]),
    content: z.string(),
  })
  .strict();
export type AgentEvalOpeningTurn = z.infer<typeof AgentEvalOpeningTurnSchema>;

export const AgentEvalScenarioDetailSchema = AgentEvalScenarioCoreSchema.extend({
  fixture: z.record(z.string(), z.unknown()),
  trigger: AgentEvalTriggerSchema,
  opening_turn: AgentEvalOpeningTurnSchema,
  golden: AgentEvalGoldenSchema,
  constraints: AgentEvalConstraintsSchema,
  /** Default field policies per records section, keyed by the section's custom_model_id, so the scoring table can show them greyed. */
  model_field_policies: z.record(z.string(), AgentEvalModelFieldPoliciesSchema),
}).strict();
export type AgentEvalScenarioDetailWire = z.infer<typeof AgentEvalScenarioDetailSchema>;

export const CreateAgentEvalScenarioRequestSchema = z
  .object({
    name: z.string().min(1).max(500),
    notes: z.string().max(20000).optional(),
    fixture: z.record(z.string(), z.unknown()),
    trigger: AgentEvalTriggerSchema,
    golden: AgentEvalGoldenSchema,
    constraints: AgentEvalConstraintsSchema.optional(),
    source: AgentEvalSourceInputSchema.optional(),
  })
  .strict();
export type CreateAgentEvalScenarioRequest = z.infer<typeof CreateAgentEvalScenarioRequestSchema>;

export const UpdateAgentEvalScenarioRequestSchema = CreateAgentEvalScenarioRequestSchema.partial();
export type UpdateAgentEvalScenarioRequest = z.infer<typeof UpdateAgentEvalScenarioRequestSchema>;

export const AgentEvalStampsSchema = z
  .object({
    worker_build: z.string().optional(),
    agent_bundle_sha256: z.string().optional(),
    scoring_config_sha256: z.string().optional(),
    model: z.string().optional(),
    withheld_tools: z.array(z.object({ name: z.string(), reason: z.string() }).strict()).optional(),
    /** Tools the agent kept in the simulation but that answered from an empty world. */
    defaulted_tools: z.array(z.string()).optional(),
  })
  .strict();
export type AgentEvalStamps = z.infer<typeof AgentEvalStampsSchema>;

export const AgentEvalResultSchema = z
  .object({
    id: z.string(),
    run_id: z.string(),
    scenario_id: z.string(),
    scenario_version: z.number().int(),
    /** True when the scenario changed after this result was scored. */
    stale: z.boolean(),
    agent_version_id: z.string().nullable(),
    agent_snapshot_sha256: z.string(),
    kind: z.enum(AGENT_EVAL_RUN_KINDS),
    status: z.enum(AGENT_EVAL_RESULT_STATUSES),
    score: z.number().min(0).max(1).nullable(),
    /** Hard behavioral contract verdict; null when the scenario has no checks or was not evaluated. `stopped` results carry no final-state score, only this. */
    passed: z.boolean().nullable(),
    agent_thread_id: z.string().nullable(),
    environment_id: z.string().nullable(),
    stamps: AgentEvalStampsSchema.nullable(),
    cost_cents: z
      .number()
      .int()
      .nullable()
      .describe("Eval spend in cents. Quovy-internal: null for workspace users and API keys."),
    duration_ms: z.number().int().nullable(),
    error_message: z.string().nullable(),
    /** When the agent thread was dispatched; null while queued. */
    started_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .strict();
export type AgentEvalResultWire = z.infer<typeof AgentEvalResultSchema>;

export const AgentEvalResultDetailSchema = AgentEvalResultSchema.extend({
  score_detail: AgentEvalScoreDetailSchema.nullable(),
  observations: AgentEvalObservationsSchema.nullable(),
  constraint_detail: AgentEvalConstraintDetailSchema.nullable(),
  /** The simulated state after the run, as stored for rescoring. */
  produced: z
    .object({
      models: z.array(z.record(z.string(), z.unknown())),
      records: z.array(z.record(z.string(), z.unknown())),
      tasks: z.array(z.record(z.string(), z.unknown())),
      tags: z.array(z.record(z.string(), z.unknown())),
      linked_resources: z.array(z.record(z.string(), z.unknown())),
    })
    .nullable(),
}).strict();
export type AgentEvalResultDetailWire = z.infer<typeof AgentEvalResultDetailSchema>;

export const AgentEvalRunSchema = z
  .object({
    id: z.string(),
    agent_id: z.string(),
    agent_version_id: z.string().nullable(),
    agent_snapshot_sha256: z.string(),
    kind: z.enum(AGENT_EVAL_RUN_KINDS),
    status: z.enum(AGENT_EVAL_RUN_STATUSES),
    scenario_ids: z.array(z.string()),
    summary: z
      .object({
        scenario_count: z.number().int(),
        scored_count: z.number().int(),
        mean_score: z.number().min(0).max(1).nullable(),
        cost_cents: z
          .number()
          .int()
          .nullable()
          .describe("Run spend in cents. Quovy-internal: null for workspace users and API keys."),
        duration_ms: z.number().int().nullable(),
      })
      .strict()
      .nullable(),
    error_message: z.string().nullable(),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
    created_at: z.string(),
    results: z.array(AgentEvalResultSchema),
  })
  .strict();
export type AgentEvalRunWire = z.infer<typeof AgentEvalRunSchema>;

/** apps/agent chat draft shape, reused so a trial can run an unsaved draft. */
export const AgentEvalDraftSnapshotSchema = z
  .object({
    operating_procedure: z.string().max(200000).optional(),
    model: z.string().min(1).max(200),
    tools_config: z.unknown().optional(),
    output_type: z.string().max(200).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type AgentEvalDraftSnapshot = z.infer<typeof AgentEvalDraftSnapshotSchema>;

export const CreateAgentEvalRunBaseSchema = z
  .object({
    /** Defaults to the agent's current version. */
    agent_version_id: zPublicId("agent_version").optional(),
    /** Run an unsaved draft instead of a version; always a trial. */
    draft: AgentEvalDraftSnapshotSchema.optional(),
    /** Defaults to every non-archived scenario of the agent. */
    scenario_ids: z.array(zPublicId("agent_eval_scenario")).max(500).optional(),
  })
  .strict();
export const CreateAgentEvalRunRequestSchema = CreateAgentEvalRunBaseSchema.refine(
  (v) => !(v.agent_version_id && v.draft),
  { message: "Pass either agent_version_id or draft, not both." },
);
export type CreateAgentEvalRunRequest = z.infer<typeof CreateAgentEvalRunRequestSchema>;

export const AgentEvalMatrixSchema = z
  .object({
    agent_id: z.string(),
    columns: z.array(
      z
        .object({
          /** null for the draft column. */
          agent_version_id: z.string().nullable(),
          version_number: z.number().int().nullable(),
          label: z.string(),
          is_current: z.boolean(),
          agent_snapshot_sha256: z.string().nullable(),
          mean_score: z.number().min(0).max(1).nullable(),
          scored_count: z.number().int(),
          scenario_count: z.number().int(),
        })
        .strict(),
    ),
    rows: z.array(
      z
        .object({
          scenario: AgentEvalScenarioSchema,
          cells: z.array(
            z
              .object({
                agent_version_id: z.string().nullable(),
                result_id: z.string().nullable(),
                score: z.number().min(0).max(1).nullable(),
                passed: z.boolean().nullable(),
                status: z.enum(AGENT_EVAL_RESULT_STATUSES).nullable(),
                stale: z.boolean(),
              })
              .strict(),
          ),
        })
        .strict(),
    ),
  })
  .strict();
export type AgentEvalMatrixWire = z.infer<typeof AgentEvalMatrixSchema>;
